'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Pencil, Check, X, Search } from 'lucide-react';
import { getAppSetting, setAppSetting } from '@/lib/appSettings';
import { parseLinks, hostOf, pathSummary, type Row } from '@/lib/links';

// 跨ぎ (localhost / 192.168.11.50) で共有したいので app_settings (SQLite) に保存する。
const STORAGE_KEY = 'top_page_links';

/**
 * トップページの「Links」タブ。HTML リンクをまとめて貼っておくだけの箱。
 * 編集は 1行1リンクのテキストエリアで一括 (追加/削除/並べ替えが全て text 編集で完結する)。
 */
export default function LinkBoard() {
  const [raw, setRaw] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const v = await getAppSetting(STORAGE_KEY);
      if (cancelled) return;
      setRaw(v ?? '');
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const rows = useMemo(() => parseLinks(raw), [raw]);
  const linkCount = useMemo(() => rows.filter(r => r.kind === 'link').length, [rows]);

  // 検索フィルタ。一致リンク＋その見出しだけを残す (見出しだけが残ることはない)。
  const displayRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    const out: Row[] = [];
    let header: Row | null = null;
    let headerShown = false;
    for (const r of rows) {
      if (r.kind === 'heading') { header = r; headerShown = false; continue; }
      if (
        r.label.toLowerCase().includes(q) ||
        r.url.toLowerCase().includes(q) ||
        r.desc.toLowerCase().includes(q)
      ) {
        if (header && !headerShown) { out.push(header); headerShown = true; }
        out.push(r);
      }
    }
    return out;
  }, [rows, query]);

  const openEditor = useCallback(() => { setDraft(raw); setEditing(true); }, [raw]);
  const saveEditor = useCallback(() => {
    setRaw(draft);
    void setAppSetting(STORAGE_KEY, draft);
    setEditing(false);
  }, [draft]);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center text-[var(--text-muted)] text-sm">
        読み込み中...
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* ツールバー (検索 + 編集切替) */}
      <div className="flex items-center gap-2 px-4 md:px-6 py-2 border-b border-[var(--border)]">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)] pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="リンクを検索…"
            disabled={editing}
            className="w-full rounded-md bg-[var(--bg-secondary)] border border-[var(--border)] pl-8 pr-7 py-1.5 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--accent)] disabled:opacity-40"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
              title="クリア"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <span className="text-xs text-[var(--text-muted)] whitespace-nowrap">{linkCount} 件</span>
        {!editing ? (
          <button
            type="button"
            onClick={openEditor}
            className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[var(--border)] text-sm text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)] hover:text-[var(--text-primary)] transition-colors"
            title="編集 (1行1リンク)"
          >
            <Pencil className="w-3.5 h-3.5" />
            編集
          </button>
        ) : (
          <div className="shrink-0 flex items-center gap-1.5">
            <button
              type="button"
              onClick={saveEditor}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[var(--accent)] text-white text-sm hover:bg-[var(--accent-hover)] transition-colors"
            >
              <Check className="w-3.5 h-3.5" />
              保存
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[var(--border)] text-sm text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)] transition-colors"
            >
              <X className="w-3.5 h-3.5" />
              キャンセル
            </button>
          </div>
        )}
      </div>

      {editing ? (
        <div className="flex-1 min-h-0 flex flex-col gap-2 p-4 md:p-6">
          <textarea
            value={draft}
            onChange={e => setDraft(e.target.value)}
            placeholder={
              'ブックマーク1件 = 2行。\n' +
              'ラベル https://example.com\n' +
              'ここに解説を書く (省略可)\n\n' +
              '# で始まる行はセクション見出し。'
            }
            className="flex-1 min-h-0 w-full resize-none rounded-md bg-[var(--bg-secondary)] border border-[var(--border)] px-3 py-2 text-sm leading-relaxed text-[var(--text-primary)] font-mono focus:outline-none focus:border-[var(--accent)]"
          />
          <p className="text-xs text-[var(--text-muted)] leading-relaxed shrink-0">
            1件=2行。<strong className="text-[var(--text-secondary)]">1行目</strong>が URL の行 (最初の URL を拾い、手前にテキストがあればラベル。
            <code className="mx-1">[ラベル](URL)</code>形式も可)、
            <strong className="text-[var(--text-secondary)]">次の行</strong>が解説になります (省略可・複数行可)。
            <code className="mx-1"># 見出し</code>でセクション分け。
          </p>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          {rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-[var(--text-muted)] gap-3">
              <p className="text-sm">リンクがありません</p>
              <button
                onClick={openEditor}
                className="px-4 py-2 rounded-md border border-[var(--border)] text-sm hover:bg-[var(--bg-elevated)] transition-colors"
              >
                リンクを貼り付ける
              </button>
            </div>
          ) : displayRows.length === 0 ? (
            <div className="flex items-center justify-center h-full text-[var(--text-muted)] text-sm">
              「{query}」に一致するリンクはありません。
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              {displayRows.map((r, i) =>
                r.kind === 'heading' ? (
                  <h2
                    key={`h-${i}`}
                    className="mt-4 first:mt-0 mb-1 text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)] border-b border-[var(--border)] pb-1"
                  >
                    {r.label}
                  </h2>
                ) : (
                  <a
                    key={`l-${i}`}
                    href={r.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={r.url}
                    className="group flex flex-col gap-0.5 px-3 py-2 rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] hover:border-[var(--accent)] hover:bg-[var(--bg-elevated)] transition-colors"
                  >
                    {/* 1行目: タイトル + ホスト */}
                    <div className="flex items-baseline gap-2 min-w-0">
                      <span className="text-sm font-medium text-[var(--text-primary)] group-hover:text-[var(--accent)] truncate">
                        {r.label}
                      </span>
                      {r.explicitLabel && (
                        <span className="ml-auto shrink-0 text-[11px] text-[var(--text-muted)]">
                          {hostOf(r.url)}
                        </span>
                      )}
                    </div>
                    {/* 2行目: 解説。無ければ URL のパスを薄く出して行数を揃える */}
                    <span
                      className={`text-xs leading-snug whitespace-pre-line break-words ${
                        r.desc ? 'text-[var(--text-secondary)]' : 'text-[var(--text-muted)] truncate'
                      }`}
                    >
                      {r.desc || pathSummary(r.url)}
                    </span>
                  </a>
                )
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
